import { InfiniteListBase, useListContext } from "ra-core";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Truck } from "lucide-react";
import { LOAD_STATUS_CHOICES } from "./LoadInputs";

// ---------------------------------------------------------------------------
// Shared load table — "counterparty" column swaps based on perspective
// ---------------------------------------------------------------------------

const LoadsTable = ({
  emptyText,
  counterpartyLabel,
  counterpartyKey,
}: {
  emptyText: string;
  counterpartyLabel: string;
  counterpartyKey: "vendor_company_name" | "customer_company_name";
}) => {
  const { data, isPending } = useListContext();

  if (isPending) return null;

  if (!data || data.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">{emptyText}</p>;
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm min-w-[600px]">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="text-left p-2 font-medium">Load #</th>
            <th className="text-left p-2 font-medium">Main Commodity</th>
            <th className="text-left p-2 font-medium">Status</th>
            <th className="text-left p-2 font-medium">Pickup Date</th>
            <th className="text-left p-2 font-medium">{counterpartyLabel}</th>
            <th className="text-right p-2 font-medium">Profit (USD)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((load: any) => {
            const statusLabel =
              LOAD_STATUS_CHOICES.find((s) => s.id === load.status)?.name ??
              load.status;
            return (
              <tr key={load.id} className="border-b hover:bg-muted/50">
                <td className="p-2 whitespace-nowrap">
                  <Link
                    to={`/loads/${load.id}/show`}
                    className="font-medium text-primary hover:underline"
                  >
                    {load.load_number || "—"}
                  </Link>
                </td>
                <td className="p-2">{load.main_commodity || "—"}</td>
                <td className="p-2 whitespace-nowrap">
                  {statusLabel ? (
                    <Badge variant="outline">{statusLabel}</Badge>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="p-2 whitespace-nowrap">
                  {load.pickup_date
                    ? new Date(load.pickup_date).toLocaleDateString()
                    : "—"}
                </td>
                <td className="p-2 whitespace-nowrap">
                  {load[counterpartyKey] || "—"}
                </td>
                <td className="p-2 whitespace-nowrap text-right">
                  {load.profit_usd != null
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                      }).format(load.profit_usd)
                    : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export const CompanyLoadsTab = ({ companyId }: { companyId: number }) => (
  <div className="flex flex-col gap-8 pt-1">
    <section>
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
        <Truck className="h-4 w-4" />
        Loads as Vendor
      </h3>
      <InfiniteListBase
        resource="loads_summary"
        filter={{ "vendor_company_id@eq": companyId }}
        sort={{ field: "created_at", order: "DESC" }}
        perPage={25}
        storeKey={false}
      >
        <LoadsTable
          emptyText="No loads where this company is a vendor."
          counterpartyLabel="Customer"
          counterpartyKey="customer_company_name"
        />
      </InfiniteListBase>
    </section>

    <section>
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
        <Truck className="h-4 w-4" />
        Loads as Customer
      </h3>
      <InfiniteListBase
        resource="loads_summary"
        filter={{ "customer_company_id@eq": companyId }}
        sort={{ field: "created_at", order: "DESC" }}
        perPage={25}
        storeKey={false}
      >
        <LoadsTable
          emptyText="No loads where this company is a customer."
          counterpartyLabel="Vendor"
          counterpartyKey="vendor_company_name"
        />
      </InfiniteListBase>
    </section>
  </div>
);
